/**
 * GoalFormDialog — F3 Wave A + CCA-M3
 *
 * Create/edit dialog for Goals. Uses a custom Anchor toggle with FormSheet.
 *
 * CCA-M3 additions:
 *   - "Draft with AI" + "Refine" affordances (AC-CCA-25)
 *   - "Company Context" tab (AC-CCA-26)
 *   - Alignment check on-demand + on-save non-blocking warning (AC-CCA-27)
 *
 * Tabs: Details | Company Context (mirror OutcomeFormDialog M2 pattern)
 * AI output lands in form fields for review/edit before saving (nothing auto-committed).
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import { useFormSnapshot } from '@/hooks/useFormSnapshot';
import { FormSheet } from '@/components/ui/FormSheet';
import type { TabDef } from '@/components/ui/FormSheet';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Select } from '@/components/ui/Select';
import { Alert } from '@/components/ui/Alert';
import { showToast } from '@/components/ui/Toast';
import { t } from '@/lib/i18n';
import { useRorosActors } from '@/hooks/roros/actors';
import { useRorosOutcomes } from '@/hooks/roros/artifacts';
import { useAlignmentCheck, useAlignmentAcknowledgeGoal, useAlignmentAcknowledgements } from '@/hooks/roros/alignment';
import { useGoalDraft, useGoalRefine } from '@/hooks/roros/goal-draft';
import { AlignmentWarnings } from '@/components/roros/alignment/AlignmentWarnings';
import { CompanyContextPanel } from '@/components/roros/alignment/CompanyContextPanel';
import type { AlignmentConflict } from '@/services/roros/alignment.service';
import type { RorosGoalDTO, GoalStatus, GoalParentType, CreateGoalPayload, UpdateGoalPayload } from '@/types/roros/goals';
import type { RorosDomainDTO } from '@/types/roros/domains';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: GoalStatus; label: string }[] = [
  { value: 'active', label: t('active') },
  { value: 'achieved', label: t('achieved') },
  { value: 'missed', label: t('missed') },
  { value: 'archived', label: t('archived') },
];

// ─── Snapshot type & diff ─────────────────────────────────────────────────────

/**
 * Captures all editable form state for dirty-detection via FormSheet.
 * FormSheet computes isDirty = Object.values(diff(baseline, current)).some(Boolean).
 */
type GoalSnapshot = {
  title: string;
  anchorType: GoalParentType | '';
  parentOutcomeId: string;
  targetDate: string;
  ownerActorId: string;
  qualitativeAssessment: string;
  description: string;
  domainId: string;
  status: GoalStatus;
};

function diffGoalSnapshots(
  base: GoalSnapshot,
  cur: GoalSnapshot,
): Partial<Record<keyof GoalSnapshot, boolean>> {
  return {
    title: base.title !== cur.title,
    anchorType: base.anchorType !== cur.anchorType,
    parentOutcomeId: base.parentOutcomeId !== cur.parentOutcomeId,
    targetDate: base.targetDate !== cur.targetDate,
    ownerActorId: base.ownerActorId !== cur.ownerActorId,
    qualitativeAssessment: base.qualitativeAssessment !== cur.qualitativeAssessment,
    description: base.description !== cur.description,
    domainId: base.domainId !== cur.domainId,
    status: base.status !== cur.status,
  };
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface GoalFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateGoalPayload | UpdateGoalPayload) => void;
  /**
   * CCA-M3-M2 fix: the parent passes the newly-created goal's id back to the
   * dialog after a successful CREATE so the post-save Acknowledge button is
   * enabled for the alignment warning. Mirror of DecisionFormDialog.savedDecisionId
   * pattern, adapted for the GoalsPage parent-owns-mutation architecture.
   * Null/undefined on the edit path (savedGoalId is set from editingGoal.id).
   */
  createdGoalId?: string | null;
  editingGoal: RorosGoalDTO | null;
  domains: RorosDomainDTO[];
  /** The UUID of the Strategy domain — pre-selected on create. */
  strategyDomainId: string;
  isLoading?: boolean;
  /** URL routing pilot (G3): when set, FormSheet syncs ?urlKey=urlId in the URL. */
  urlKey?: string;
  /** The id value written to the URL param. Required when urlKey is set. */
  urlId?: string;
}

// ─── Component ────────────────────────────────────────────────────────────────

export function GoalFormDialog({
  isOpen,
  onClose,
  onSubmit,
  createdGoalId,
  editingGoal,
  domains,
  strategyDomainId,
  isLoading = false,
  urlKey,
  urlId,
}: GoalFormDialogProps) {
  const isEditing = !!editingGoal;

  // CCA-M3: AI draft/refine + alignment
  const goalDraftMutation = useGoalDraft();
  const goalRefineMutation = useGoalRefine();
  const alignmentCheckMutation = useAlignmentCheck();
  const alignmentAcknowledgeMutation = useAlignmentAcknowledgeGoal();
  // AC-CCA-28: fetch prior acks from audit log (edit mode only; skipped when no editingGoal.id)
  const { seededIds: seededAcknowledgedIds } = useAlignmentAcknowledgements(
    editingGoal?.id ?? null,
    'goal',
  );

  const [aiPromptText, setAiPromptText] = useState('');
  const [aiDrafting, setAiDrafting] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [alignmentConflicts, setAlignmentConflicts] = useState<AlignmentConflict[]>([]);
  const [acknowledgedPrincipleIds, setAcknowledgedPrincipleIds] = useState<Set<string>>(new Set());
  const [savedGoalId, setSavedGoalId] = useState<string | null>(editingGoal?.id ?? null);
  // CCA-M3-M2 fix: when the parent passes back a createdGoalId after a successful
  // create-save, store it so acknowledgeEnabled becomes true for post-save conflicts.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (createdGoalId) setSavedGoalId(createdGoalId);
  }, [createdGoalId]);

  // AC-CCA-28: Seed acknowledgedPrincipleIds with prior acks (edit mode only).
  // Runs when seededAcknowledgedIds resolves from the audit log query.
  // Bail (return prev unchanged) when no new ids would be added — prevents
  // React from re-rendering and retriggering this effect (infinite loop guard).
  React.useEffect(() => {
    if (seededAcknowledgedIds.size > 0) {
      setAcknowledgedPrincipleIds((prev) => {
        const merged = new Set(prev);
        let changed = false;
        seededAcknowledgedIds.forEach((id) => {
          if (!merged.has(id)) { merged.add(id); changed = true; }
        });
        return changed ? merged : prev;
      });
    }
  }, [seededAcknowledgedIds]);

  // ─── Form state via useFormSnapshot (F-Refinement-10) ─────────────────────
  const { state, setField, baseline, current, setBaseline } = useFormSnapshot<GoalSnapshot>({
    initialFactory: () => editingGoal
      ? {
          title: editingGoal.title,
          anchorType: (editingGoal.parentType ?? '') as GoalParentType | '',
          parentOutcomeId: editingGoal.parentOutcomeId ?? '',
          targetDate: editingGoal.targetDate ? editingGoal.targetDate.slice(0, 10) : '',
          ownerActorId: editingGoal.ownerActorId ?? '',
          qualitativeAssessment: editingGoal.qualitativeAssessment,
          description: editingGoal.description ?? '',
          domainId: editingGoal.domainId,
          status: editingGoal.status,
        }
      : {
          title: '',
          anchorType: 'vision',
          parentOutcomeId: '',
          targetDate: '',
          ownerActorId: '',
          qualitativeAssessment: '',
          description: '',
          domainId: strategyDomainId,
          status: 'active',
        },
    isOpen,
    depKey: editingGoal?.id ?? null,
  });
  const { title, anchorType, parentOutcomeId, targetDate, ownerActorId, qualitativeAssessment, description, domainId, status } = state;

  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  // Reset validation errors on open.
  useEffect(() => {
    if (isOpen) setErrors({});
  }, [isOpen, editingGoal?.id]);

  // ─── External data for selects ────────────────────────────────────────────
  const actorsQuery = useRorosActors({ type: 'human' });
  const actors = actorsQuery.data ?? [];

  // Outcomes for parent dropdown (only needed when anchorType === 'outcome')
  const outcomesQuery = useRorosOutcomes(domainId || strategyDomainId, { status: 'active' });
  const outcomes = (outcomesQuery.data ?? []) as Array<{ id: string; title: string }>;
  const outcomeOptions = useMemo(
    () => [
      { value: '__none__', label: t('Select an outcome…') },
      ...outcomes.map((o) => ({ value: o.id, label: o.title })),
    ],
    [outcomes],
  );

  // ─── Validation & save ────────────────────────────────────────────────────
  const handleSave = () => {
    const nextErrors: Partial<Record<string, string>> = {};
    if (!title.trim()) nextErrors.title = t('Title is required');
    if (!targetDate) nextErrors.targetDate = t('Target date is required');
    if (!qualitativeAssessment.trim()) nextErrors.qualitativeAssessment = t('Qualitative assessment is required');
    if (!domainId) nextErrors.domainId = t('Domain is required');

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    // Warn (non-blocking) if no anchor is set
    if (!anchorType) {
      showToast.warning(t('No anchor set — this goal is not linked to a Vision or Outcome.'));
    }

    const payload: CreateGoalPayload | UpdateGoalPayload = {
      title: title.trim(),
      description: description.trim() || null,
      qualitative_assessment: qualitativeAssessment.trim(),
      target_date: targetDate || null,
      parent_type: (anchorType as GoalParentType) || null,
      parent_outcome_id: anchorType === 'outcome' ? (parentOutcomeId || null) : null,
      owner_actor_id: ownerActorId || null,
      domainId: domainId,  // A1: API uses camelCase domainId
      ...(isEditing ? { status } : {}),
    };

    // AC-CCA-27: goal ALWAYS saves first; alignment check is non-blocking.
    onSubmit(payload);

    // Run alignment check after save (non-blocking — goal already saved)
    alignmentCheckMutation.mutate(
      { subject: { title: title.trim(), description: qualitativeAssessment.trim() } },
      {
        onSuccess: (result) => {
          if (result.status === 'ok' && result.conflicts.length > 0) {
            setAlignmentConflicts(result.conflicts);
          }
        },
        onError: () => { /* LLM error → swallow silently, goal already saved */ },
      },
    );
  };

  // ─── AI Draft handler ─────────────────────────────────────────────────────
  const handleAiDraft = async () => {
    const prompt = aiPromptText.trim();
    if (!prompt) return;
    setAiDrafting(true);
    setAiError(null);
    try {
      const result = await goalDraftMutation.mutateAsync({ prompt });
      if (result) {
        if (result.title) setField('title', result.title);
        if (result.qualitative_assessment) setField('qualitativeAssessment', result.qualitative_assessment);
        if (result.description) setField('description', result.description);
        setAiPromptText('');
      } else {
        setAiError(t('AI draft unavailable — no AI Operator configured.'));
      }
    } catch {
      setAiError(t('Draft failed. Check AI settings or try again.'));
    } finally {
      setAiDrafting(false);
    }
  };

  // ─── AI Refine handler ───────────────────────────────────────────────────
  const handleAiRefine = async () => {
    if (!title.trim() && !qualitativeAssessment.trim()) return;
    setAiDrafting(true);
    setAiError(null);
    try {
      const result = await goalRefineMutation.mutateAsync({
        title: title.trim(),
        qualitative_assessment: qualitativeAssessment.trim(),
        description: description.trim() || null,
      });
      if (result) {
        if (result.title) setField('title', result.title);
        if (result.qualitative_assessment) setField('qualitativeAssessment', result.qualitative_assessment);
        if (result.description !== undefined) setField('description', result.description ?? '');
      } else {
        setAiError(t('Refine unavailable — no AI Operator configured.'));
      }
    } catch {
      setAiError(t('Refine failed. Check AI settings or try again.'));
    } finally {
      setAiDrafting(false);
    }
  };

  // ─── Domain options ───────────────────────────────────────────────────────
  const domainOptions = useMemo(
    () => domains.map((d) => ({ value: d.id, label: d.name })),
    [domains],
  );

  // ─── Actor options ────────────────────────────────────────────────────────
  const actorOptions = useMemo(
    () => [
      { value: '__none__', label: t('Unassigned') },
      ...actors.map((a) => ({ value: a.id, label: a.name })),
    ],
    [actors],
  );

  const dialogTitle = isEditing ? t('Edit Goal') : t('New Goal');

  // ─── Tab: Details (form fields + AI draft + alignment) ───────────────────
  const detailsTabContent = (
    <div className="space-y-4">
        {/* CCA-M3 AC-CCA-25: AI Draft section */}
        <div className="flex items-center justify-between gap-3 rounded-md border border-primary-200 bg-primary-50 px-3 py-2">
          <div className="flex-1 min-w-0">
            {aiError && (
              <p className="text-xs text-error-600 mb-1" data-testid="goal-ai-error">{aiError}</p>
            )}
            <Input
              value={aiPromptText}
              onChange={(e) => setAiPromptText(e.target.value)}
              placeholder={t('Describe your goal (AI will draft it)…')}
              data-testid="goal-ai-prompt-input"
            />
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleAiDraft}
              disabled={aiDrafting || !aiPromptText.trim()}
              isLoading={aiDrafting && !!aiPromptText.trim()}
              leftIcon={<Sparkles className="h-3.5 w-3.5" />}
              data-testid="goal-ai-draft-button"
            >
              {t('Draft with AI')}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleAiRefine}
              disabled={aiDrafting || (!title.trim() && !qualitativeAssessment.trim())}
              isLoading={aiDrafting && !aiPromptText.trim()}
              leftIcon={<Sparkles className="h-3.5 w-3.5" />}
              data-testid="goal-ai-refine-button"
            >
              {t('Refine')}
            </Button>
          </div>
        </div>

        {/* Global error summary */}
        {Object.keys(errors).length > 0 && (
          <Alert variant="error" data-testid="goal-error-summary">
            {t('Please fix the errors below before saving.')}
          </Alert>
        )}

        {/* 1. Title */}
        <div>
          <label className="text-sm font-medium text-foreground" htmlFor="goal-title">
            {t('Title')} <span className="text-destructive">*</span>
          </label>
          <Input
            id="goal-title"
            value={title}
            onChange={(e) => setField('title', e.target.value)}
            placeholder={t('e.g. Reach 1,000 paying customers')}
            data-testid="goal-title-input"
          />
          {errors.title && (
            <p className="text-xs text-destructive mt-1">{errors.title}</p>
          )}
        </div>

        {/* 2. Anchor — inline buttons use the testids expected by Playwright */}
        <div>
          <span className="text-sm font-medium text-foreground block mb-1">{t('Anchor')}</span>
          <div className="flex gap-2 mb-2">
            <Button
              type="button"
              variant={anchorType === 'vision' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => { setField('anchorType', 'vision'); setField('parentOutcomeId', ''); }}
              data-testid="goal-anchor-vision"
            >
              {t('Under Vision')}
            </Button>
            <Button
              type="button"
              variant={anchorType === 'outcome' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setField('anchorType', 'outcome')}
              data-testid="goal-anchor-outcome"
            >
              {t('Under an Outcome')}
            </Button>
            <Button
              type="button"
              variant={anchorType === '' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => { setField('anchorType', ''); setField('parentOutcomeId', ''); }}
              data-testid="goal-anchor-none"
            >
              {t('None')}
            </Button>
          </div>

          {/* Outcome dropdown — shown only when 'outcome' anchor type is selected */}
          {anchorType === 'outcome' && (
            <div className="mt-2">
              <label
                className="text-sm text-muted-foreground block mb-1"
                htmlFor="goal-parent-outcome"
              >
                {t('Parent Outcome')}
              </label>
              <Select
                id="goal-parent-outcome"
                value={parentOutcomeId || '__none__'}
                onChange={(e) => setField('parentOutcomeId', e.target.value === '__none__' ? '' : e.target.value)}
                options={outcomeOptions}
                data-testid="goal-parent-outcome-select"
              />
              {outcomeOptions.length <= 1 && (
                <p className="text-xs text-muted-foreground mt-1">
                  {t('No active outcomes in this domain yet.')}
                </p>
              )}
            </div>
          )}

          {anchorType === 'vision' && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('This goal is anchored to the organisation Vision.')}
            </p>
          )}
          {anchorType === '' && (
            <p className="text-xs text-muted-foreground mt-1">
              {t('No anchor — this goal is standalone (a warning will appear on save).')}
            </p>
          )}
        </div>

        {/* 3. Target date */}
        <div>
          <label className="text-sm font-medium text-foreground block mb-1" htmlFor="goal-target-date">
            {t('Target Date')} <span className="text-destructive">*</span>
          </label>
          <Input
            id="goal-target-date"
            type="date"
            value={targetDate}
            onChange={(e) => setField('targetDate', e.target.value)}
            data-testid="goal-target-date-input"
          />
          {errors.targetDate && (
            <p className="text-xs text-destructive mt-1">{errors.targetDate}</p>
          )}
        </div>

        {/* 4. Accountable actor */}
        <div>
          <label className="text-sm font-medium text-foreground block mb-1" htmlFor="goal-owner">
            {t('Accountable')}
          </label>
          <Select
            id="goal-owner"
            value={ownerActorId || '__none__'}
            onChange={(e) => setField('ownerActorId', e.target.value === '__none__' ? '' : e.target.value)}
            options={actorOptions}
            data-testid="goal-owner-select"
          />
        </div>

        {/* 5. Qualitative assessment */}
        <div>
          <label className="text-sm font-medium text-foreground block mb-1" htmlFor="goal-qa">
            {t('Qualitative Assessment')} <span className="text-destructive">*</span>
          </label>
          <Textarea
            id="goal-qa"
            value={qualitativeAssessment}
            onChange={(e) => setField('qualitativeAssessment', e.target.value)}
            placeholder={t('What does success look like qualitatively?')}
            rows={3}
            data-testid="goal-qa-input"
          />
          {errors.qualitativeAssessment && (
            <p className="text-xs text-destructive mt-1">{errors.qualitativeAssessment}</p>
          )}
        </div>

        {/* 6. Description */}
        <div>
          <label className="text-sm font-medium text-foreground block mb-1" htmlFor="goal-description">
            {t('Description')}
          </label>
          <Textarea
            id="goal-description"
            value={description}
            onChange={(e) => setField('description', e.target.value)}
            placeholder={t('Optional context or background')}
            rows={2}
            data-testid="goal-description-input"
          />
        </div>

        {/* 7. Domain */}
        <div>
          <label className="text-sm font-medium text-foreground block mb-1" htmlFor="goal-domain">
            {t('Domain')} <span className="text-destructive">*</span>
          </label>
          <Select
            id="goal-domain"
            value={domainId || '__none__'}
            onChange={(e) => setField('domainId', e.target.value === '__none__' ? '' : e.target.value)}
            options={[
              { value: '__none__', label: t('Select a domain…') },
              ...domainOptions,
            ]}
            data-testid="goal-domain-select"
          />
          {errors.domainId && (
            <p className="text-xs text-destructive mt-1">{errors.domainId}</p>
          )}
        </div>

        {/* 8. Status (edit only) */}
        {isEditing && (
          <div>
            <label className="text-sm font-medium text-foreground block mb-1" htmlFor="goal-status">
              {t('Status')}
            </label>
            <Select
              id="goal-status"
              value={status}
              onChange={(e) => setField('status', e.target.value as GoalStatus)}
              options={STATUS_OPTIONS}
              data-testid="goal-status-select"
            />
          </div>
        )}

        {/* CCA-M3 AC-CCA-27: alignment warnings (non-blocking; appear after save) */}
        {alignmentConflicts.length > 0 && (
          <div className="pt-2 border-t border-warning-200">
            <AlignmentWarnings
              conflicts={alignmentConflicts}
              acknowledgedIds={acknowledgedPrincipleIds}
              isAcknowledging={alignmentAcknowledgeMutation.isPending}
              acknowledgeEnabled={!!(savedGoalId ?? editingGoal?.id)}
              onAcknowledge={(principleId) => {
                const goalId = savedGoalId ?? editingGoal?.id;
                if (!goalId) return;
                alignmentAcknowledgeMutation.mutate(
                  { goalId, principleId },
                  {
                    onSuccess: () => {
                      setAcknowledgedPrincipleIds((prev) => new Set([...prev, principleId]));
                    },
                  },
                );
              }}
              data-testid="goal-alignment-warnings"
            />
          </div>
        )}

        {/* CCA-M3 AC-CCA-27: on-demand alignment check button */}
        {alignmentConflicts.length === 0 && (
          <div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                if (!title.trim()) return;
                alignmentCheckMutation.mutate(
                  { subject: { title: title.trim(), description: qualitativeAssessment.trim() || undefined } },
                  {
                    onSuccess: (result) => {
                      if (result.status === 'ok' && result.conflicts.length > 0) {
                        setAlignmentConflicts(result.conflicts);
                      }
                    },
                    onError: () => { /* soft failure — button returns to idle */ },
                  },
                );
              }}
              disabled={alignmentCheckMutation.isPending || !title.trim()}
              data-testid="goal-check-alignment"
            >
              {alignmentCheckMutation.isPending ? t('Checking…') : t('Check alignment')}
            </Button>
          </div>
        )}
      </div>
  );

  // ─── Tab: Company Context ─────────────────────────────────────────────────
  const companyContextTabContent = (
    <div className="pt-2">
      <CompanyContextPanel data-testid="goal-company-context-panel" />
    </div>
  );

  // ─── Tabs definition ──────────────────────────────────────────────────────
  const goalTabs: Array<TabDef<GoalSnapshot>> = [
    {
      key: 'details',
      label: t('Details'),
      fields: ['title', 'anchorType', 'parentOutcomeId', 'targetDate', 'ownerActorId', 'qualitativeAssessment', 'description', 'domainId', 'status'] as Array<keyof GoalSnapshot>,
      content: detailsTabContent,
      testId: 'goal-tab-details',
    },
    {
      key: 'context',
      label: t('Company Context'),
      content: companyContextTabContent,
      testId: 'goal-tab-context',
    },
  ];

  return (
    <FormSheet<GoalSnapshot>
      isOpen={isOpen}
      onClose={onClose}
      title={dialogTitle}
      mode={isEditing ? 'edit' : 'create'}
      baseline={baseline}
      current={current}
      diff={diffGoalSnapshots}
      onSubmit={handleSave}
      isSubmitting={isLoading}
      submitLabel={isEditing ? t('Save') : t('Create Goal')}
      submittingLabel={t('Saving…')}
      submitTestId="goal-save-button"
      width="md"
      urlKey={urlKey}
      urlId={urlId}
      tabs={goalTabs}
      tabsAriaLabel={t('Goal editor sections')}
    />
  );
}
